<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import Submission from '@/api/Submission'
import Provider from '@/api/Provider'
import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
import FileAttachmentItem from '@/components/FileAttachmentItem.vue'
import { useMainStore } from '@/stores/main'

const props = defineProps({
  prefillItems: {
    type: Array,
    default: () => []
  },
  lockedItems: {
    type: Array,
    default: () => []
  },
  hideItems: {
    type: Boolean,
    default: false
  },
  allowedCategories: {
    type: Array,
    default: null
  },
  maxChars: {
    type: Number,
    default: 3000
  },
  placeholder: {
    type: String,
    default: "What's up?"
  },
  parentSubmission: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['modal-close', 'submission-created', 'comment-click'])
const mainStore = useMainStore()

// Form state
const content = ref('')
const items = ref([])
const loading = ref(false)
const error = ref(null)
const success = ref(false)

// Dropdown data
const categories = ref([])
const providers = ref([])
const providersLoading = ref(true)

// UI state
const showBulkAddModal = ref(false)
const bulkAddText = ref('')
const bulkAddPreview = ref([])
const bulkAddFilename = ref('bulk')

// File upload state
const uploadedFiles = ref([])
const fileMetadata = ref([])
const uploadedAttachments = ref([])
const uploadingFiles = ref(false)
const fileInput = ref(null)
const isDragging = ref(false)

// Item type detection
function detectItemType(value) {
  const trimmed = value.trim()

  // IP detection (IPv4 and IPv6, including defanged)
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(?:\.|\[\.\])){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])$/
  const ipv6Regex = /^([0-9a-fA-F]{0,4}:){7}[0-9a-fA-F]{0,4}$/

  if (ipv4Regex.test(trimmed) || ipv6Regex.test(trimmed)) {
    return 'IP'
  }

  // URL detection (must have protocol)
  if (trimmed.match(/^https?:\/\//)) {
    return 'URL'
  }

  // Domain detection
  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/
  if (domainRegex.test(trimmed)) {
    return 'Domain'
  }

  return 'Unknown'
}

// Enhanced extraction with word boundaries
function extractItemsFromText(text) {
  if (!text || text.trim().length === 0) return []

  const items = []
  const seen = new Set()

  // Detect IPs (including defanged like 1[.]2[.]3[.]4)
  const ipRegex = /\b(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(?:\.|\[\.\])){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\b/g
  let match
  while ((match = ipRegex.exec(text)) !== null) {
    const cleanValue = match[0].replace(/\[|\]/g, '') // Remove defang brackets
    if (!seen.has(cleanValue)) {
      seen.add(cleanValue)
      items.push({
        value: cleanValue,
        type: 'IP',
        source: 'auto'
      })
    }
  }

  // Detect URLs (must have protocol) - do this before domains to avoid double-matching
  const urlRegex = /\bhttps?:\/\/[^\s]+/g
  while ((match = urlRegex.exec(text)) !== null) {
    const value = match[0]
    if (!seen.has(value)) {
      seen.add(value)
      items.push({
        value: value,
        type: 'URL',
        source: 'auto'
      })
    }
  }

  // Detect domains (after removing URLs from consideration)
  const domainRegex = /\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\b/g
  while ((match = domainRegex.exec(text)) !== null) {
    const value = match[0]
    // Skip if it's part of a URL we already captured
    const isPartOfUrl = items.some(item => item.type === 'URL' && item.value.includes(value))
    if (!seen.has(value) && !isPartOfUrl) {
      seen.add(value)
      items.push({
        value: value,
        type: 'Domain',
        source: 'auto'
      })
    }
  }

  return items
}

// Auto-resize textarea
const textareaEl = ref(null)
function adjustTextareaHeight() {
  if (textareaEl.value) {
    textareaEl.value.style.height = 'auto'
    textareaEl.value.style.height = textareaEl.value.scrollHeight + 'px'
  }
}

// Merge auto-detected items with existing items
function mergeAutoDetectedItems(detectedItems) {
  // First: preserve all locked items (highest priority)
  const lockedItems = items.value.filter(item => item.locked)

  // Second: preserve manual items
  const manualItems = items.value.filter(item => item.source === 'manual' && !item.locked)

  // Third: preserve edited auto items
  const editedAutoItems = items.value.filter(item =>
    item.source === 'auto' && !item.locked && (
      item.attribution ||
      item.category !== null ||
      item.description ||
      item.selectedProvider
    )
  )

  // Create set of values we want to keep
  const keepValues = new Set([
    ...lockedItems.map(i => i.value),
    ...manualItems.map(i => i.value),
    ...editedAutoItems.map(i => i.value)
  ])

  // Add new auto-detected items that aren't already in the list
  const newItems = [...lockedItems, ...manualItems, ...editedAutoItems]

  detectedItems.forEach(detected => {
    if (!keepValues.has(detected.value)) {
      newItems.push({
        value: detected.value,
        type: detected.type,
        source: 'auto',
        locked: false,
        expanded: false,
        attribution: '',
        category: null,
        description: '',
        providerSearch: '',
        showProviderDropdown: false,
        selectedProvider: null,
        dropdownTop: null,
        dropdownLeft: null,
        dropdownWidth: null
      })
      keepValues.add(detected.value)
    }
  })

  items.value = newItems
}

// Debounced auto-detection
const debouncedExtract = useDebounceFn(() => {
  const detected = extractItemsFromText(content.value)
  mergeAutoDetectedItems(detected)
}, 500)

watch(content, () => {
  adjustTextareaHeight()
  debouncedExtract()
})

// Character counter
const remainingChars = computed(() => {
  return props.maxChars - content.value.length
})

const isContentValid = computed(() => {
  return content.value.length <= props.maxChars
})

// Items management
const newItemValue = ref('')

function addItem() {
  const trimmed = newItemValue.value.trim()
  if (!trimmed) return

  if (items.value.length >= 100) {
    error.value = 'Maximum 100 items allowed'
    return
  }

  // Check for duplicates
  if (items.value.some(item => item.value === trimmed)) {
    error.value = 'Item already added'
    return
  }

  const itemType = detectItemType(trimmed)

  items.value.push({
    value: trimmed,
    type: itemType,
    source: 'manual',
    locked: false,
    expanded: false,
    attribution: '',
    category: null,
    description: '',
    providerSearch: '',
    showProviderDropdown: false,
    selectedProvider: null,
    dropdownTop: null,
    dropdownLeft: null,
    dropdownWidth: null
  })

  newItemValue.value = ''
  error.value = null
}

function removeItem(index) {
  // Don't remove locked items
  if (items.value[index].locked) {
    return
  }
  items.value.splice(index, 1)
}

function toggleItemExpanded(index) {
  items.value[index].expanded = !items.value[index].expanded
}

// Provider search for individual items
function handleItemProviderInput(item, event) {
  item.showProviderDropdown = true
  if (item.selectedProvider && item.providerSearch !== (item.selectedProvider.display_name || item.selectedProvider.name)) {
    item.selectedProvider = null
    item.attribution = ''
  }

  // Calculate dropdown position for fixed positioning
  if (event && event.target) {
    const rect = event.target.getBoundingClientRect()
    item.dropdownTop = rect.bottom + 4
    item.dropdownLeft = rect.left
    item.dropdownWidth = rect.width
  }
}

function handleItemProviderFocus(item, event) {
  item.showProviderDropdown = true

  // Calculate dropdown position for fixed positioning
  if (event && event.target) {
    const rect = event.target.getBoundingClientRect()
    item.dropdownTop = rect.bottom + 4
    item.dropdownLeft = rect.left
    item.dropdownWidth = rect.width
  }
}

function selectItemProvider(item, provider) {
  item.selectedProvider = provider
  item.attribution = provider.name
  item.providerSearch = provider.display_name || provider.name
  item.showProviderDropdown = false
}

function clearItemProvider(item) {
  item.selectedProvider = null
  item.attribution = ''
  item.providerSearch = ''
}

// Filter providers for item dropdown
function getFilteredProvidersForItem(item) {
  if (!item.providerSearch) {
    return providers.value
  }

  const search = item.providerSearch.toLowerCase()
  return providers.value.filter(provider => {
    const displayName = provider.display_name || provider.name
    return displayName.toLowerCase().includes(search) ||
           provider.name.toLowerCase().includes(search)
  })
}

// Form validation
const isFormValid = computed(() => {
  // Content OR items required (but not both empty)
  if (!content.value.trim() && items.value.length === 0) {
    return false
  }

  // Content length check
  if (content.value.length > props.maxChars) {
    return false
  }

  // Item descriptions length check
  for (const item of items.value) {
    if (item.description && item.description.length > 500) {
      return false
    }
  }

  // Items max check
  if (items.value.length > 100) {
    return false
  }

  return true
})

// Get filtered categories based on allowedCategories prop
const availableCategories = computed(() => {
  if (props.allowedCategories) {
    return categories.value.filter(cat => props.allowedCategories.includes(cat.value))
  }
  return categories.value
})

// File upload functions
function openFileUpload() {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

function handleFileSelect(event) {
  const files = Array.from(event.target.files || [])
  processFiles(files)
}

function processFiles(files) {
  const maxSize = 50 * 1024 * 1024 // 50MB
  const validFiles = []
  const invalidFiles = []

  files.forEach(file => {
    if (file.size > maxSize) {
      invalidFiles.push(file.name)
    } else {
      validFiles.push(file)
    }
  })

  if (invalidFiles.length > 0) {
    error.value = `Files too large (max 50MB): ${invalidFiles.join(', ')}`
    setTimeout(() => {
      if (error.value === `Files too large (max 50MB): ${invalidFiles.join(', ')}`) {
        error.value = null
      }
    }, 5000)
  }

  if (validFiles.length > 0) {
    uploadedFiles.value.push(...validFiles)
    validFiles.forEach(() => {
      fileMetadata.value.push({
        attribution: '',
        category: null,
        description: ''
      })
      uploadedAttachments.value.push(null)
    })
  }

  // Reset file input
  if (event.target) {
    event.target.value = ''
  }
}

function removeFile(index) {
  uploadedFiles.value.splice(index, 1)
  fileMetadata.value.splice(index, 1)
  uploadedAttachments.value.splice(index, 1)
}

// Drag and drop handlers
function handleDragEnter(e) {
  e.preventDefault()
  isDragging.value = true
}

function handleDragOver(e) {
  e.preventDefault()
  isDragging.value = true
}

function handleDragLeave(e) {
  e.preventDefault()
  if (e.target === e.currentTarget) {
    isDragging.value = false
  }
}

function handleDrop(e) {
  e.preventDefault()
  isDragging.value = false

  const files = Array.from(e.dataTransfer.files || [])
  processFiles(files)
}

async function uploadFiles(submissionId) {
  if (uploadedFiles.value.length === 0) {
    return
  }

  uploadingFiles.value = true

  try {
    const formData = new FormData()

    // Append each file
    uploadedFiles.value.forEach((file) => {
      formData.append('files', file)
    })

    // Append metadata as JSON string (backend expects this format)
    formData.append('file_metadata', JSON.stringify(fileMetadata.value))

    const response = await Submission.uploadFiles(submissionId, formData)

    if (response.status === 'success') {
      uploadedAttachments.value = response.data

      // Show extraction errors if any
      const errors = response.data
        .filter(att => att.extraction_error)
        .map(att => `${att.file_name}: ${att.extraction_error}`)

      if (errors.length > 0) {
        console.warn('Extraction errors:', errors)
        // Don't block submission, just log
      }
    } else {
      throw new Error(response.message || 'Failed to upload files')
    }
  } catch (err) {
    console.error('File upload error:', err)
    throw err
  } finally {
    uploadingFiles.value = false
  }
}

// Submit form
async function submitPost() {
  error.value = null

  if (!isFormValid.value) {
    error.value = 'Please ensure all fields are valid'
    return
  }

  loading.value = true

  try {
    // Step 1: Create the submission first (without files)
    const payload = {}

    // Add content if provided
    if (content.value.trim()) {
      payload.content = content.value.trim()
    }

    // Add parent_id if this is a comment
    if (props.parentSubmission) {
      payload.parent_id = props.parentSubmission.id
    }

    // Add items
    if (items.value.length > 0) {
      payload.items = items.value.map(item => {
        const itemData = {
          value: item.value,
          type: item.type
        }

        // Only include individual settings if they're set
        if (item.attribution && item.attribution.trim()) {
          itemData.attribution = item.attribution.trim()
        }

        if (item.category !== null) {
          itemData.category = item.category
        }

        if (item.description && item.description.trim()) {
          itemData.description = item.description.trim()
        }

        return itemData
      })
    }

    // Create the submission
    const response = await Submission.createPost(payload)

    if (response.status !== 'success') {
      error.value = response.message || 'Failed to create post'
      return
    }

    const submissionId = response.submission_id

    // Step 2: Upload files if any (linked to the submission)
    if (uploadedFiles.value.length > 0 && submissionId) {
      try {
        await uploadFiles(submissionId)
      } catch (uploadErr) {
        // Files failed to upload, but submission was created
        error.value = 'Post created but files failed to upload. You can try uploading them again later.'
        console.error('Upload error:', uploadErr)

        // Still consider this a partial success
        success.value = true
        emit('submission-created', response)
        mainStore.event_emit('submission-created')

        setTimeout(() => {
          emit('modal-close')
        }, 3000)
        return
      }
    }

    // Success!
    success.value = true
    emit('submission-created', response)
    mainStore.event_emit('submission-created')

    setTimeout(() => {
      emit('modal-close')
    }, 2000)
  } catch (err) {
    error.value = err.response?.data?.message || 'An error occurred while submitting'
  } finally {
    loading.value = false
  }
}

function cancel() {
  emit('modal-close')
}

// Bulk add functions
function openBulkAdd() {
  showBulkAddModal.value = true
  bulkAddText.value = ''
  bulkAddPreview.value = []
  bulkAddFilename.value = 'bulk'
}

function sanitizeFilename() {
  // Remove .txt if user types it
  if (bulkAddFilename.value.endsWith('.txt')) {
    bulkAddFilename.value = bulkAddFilename.value.slice(0, -4)
  }
  // Remove invalid filename characters
  bulkAddFilename.value = bulkAddFilename.value.replace(/[/\\?%*:|"<>]/g, '')
  // Limit length to reasonable size
  if (bulkAddFilename.value.length > 200) {
    bulkAddFilename.value = bulkAddFilename.value.slice(0, 200)
  }
}

function processBulkText() {
  const detected = extractItemsFromText(bulkAddText.value)
  bulkAddPreview.value = detected
}

function confirmBulkAdd() {
  // Format items cleanly - one per line
  const formattedContent = bulkAddPreview.value
    .map(item => item.value)
    .join('\n')

  // Create filename with .txt extension
  const filename = (bulkAddFilename.value.trim() || 'bulk') + '.txt'

  // Create Blob from text content
  const blob = new Blob([formattedContent], { type: 'text/plain' })

  // Create File object
  const file = new File([blob], filename, {
    type: 'text/plain',
    lastModified: Date.now()
  })

  // Add to uploaded files
  uploadedFiles.value.push(file)

  // Add empty metadata (user will fill via FileAttachmentItem)
  fileMetadata.value.push({
    attribution: '',
    category: null,
    description: ''
  })

  // Add null attachment (will be populated after upload)
  uploadedAttachments.value.push(null)

  // Close modal
  showBulkAddModal.value = false
}

// Load data on mount
onMounted(async () => {
  try {
    const [categoriesRes, providersRes] = await Promise.all([
      Submission.getCategories(),
      Provider.listProviders()
    ])

    categories.value = categoriesRes.data || []
    providers.value = providersRes || []

    // Load prefilled items
    if (props.prefillItems && props.prefillItems.length > 0) {
      props.prefillItems.forEach(itemValue => {
        const trimmed = itemValue.trim()
        if (trimmed) {
          const itemType = detectItemType(trimmed)
          items.value.push({
            value: trimmed,
            type: itemType,
            source: 'manual', // Prefilled items are treated as manual to prevent auto-detection from removing them
            locked: false,
            expanded: false,
            attribution: '',
            category: null,
            description: '',
            providerSearch: '',
            showProviderDropdown: false,
            selectedProvider: null,
            dropdownTop: null,
            dropdownLeft: null,
            dropdownWidth: null
          })
        }
      })
    }

    // Load locked items
    if (props.lockedItems && props.lockedItems.length > 0) {
      props.lockedItems.forEach(itemValue => {
        const trimmed = itemValue.trim()
        if (trimmed) {
          // Check if item already exists (avoid duplicates with prefillItems)
          const exists = items.value.some(item => item.value === trimmed)
          if (!exists) {
            const itemType = detectItemType(trimmed)
            items.value.push({
              value: trimmed,
              type: itemType,
              source: 'manual',
              locked: true,
              expanded: false,
              attribution: '',
              category: null,
              description: '',
              providerSearch: '',
              showProviderDropdown: false,
              selectedProvider: null,
              dropdownTop: null,
              dropdownLeft: null,
              dropdownWidth: null
            })
          } else {
            // If item exists, mark it as locked
            const existingItem = items.value.find(item => item.value === trimmed)
            if (existingItem) {
              existingItem.locked = true
              // Ensure dropdown positioning properties exist
              if (!existingItem.dropdownTop) existingItem.dropdownTop = null
              if (!existingItem.dropdownLeft) existingItem.dropdownLeft = null
              if (!existingItem.dropdownWidth) existingItem.dropdownWidth = null
            }
          }
        }
      })
    }
  } catch (err) {
    console.error('Failed to load form data:', err)
    error.value = 'Failed to load form data'
  } finally {
    providersLoading.value = false
  }

  // Auto-adjust textarea height after mounting
  setTimeout(adjustTextareaHeight, 0)
})

// Helper function to truncate text
function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}
</script>

<template>
  <div class="submission-composer">
    <div v-if="success" class="success-message">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm5.707 8.707l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 14.586l5.293-5.293a1 1 0 111.414 1.414z" fill="#10B981"/>
      </svg>
      Post created successfully!
    </div>

    <div v-else class="composer-content">
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Parent Context -->
      <div v-if="parentSubmission" class="parent-context">
        <div class="parent-context-header">
          <font-awesome-icon :icon="['fas', 'reply']" />
          Replying to {{ parentSubmission.user.name }}
        </div>
        <div class="parent-context-content">
          {{ truncateText(parentSubmission.content, 100) }}
        </div>
      </div>

      <!-- Twitter-style Header with Avatar and Textarea -->
      <div class="composer-header">
        <div class="composer-avatar">
          {{ mainStore.user?.email ? mainStore.user.email.charAt(0).toUpperCase() : 'U' }}
        </div>
        <div class="composer-main">
          <!-- Drag and Drop Zone -->
          <div
            class="drag-drop-zone"
            :class="{ 'dragging': isDragging }"
            @dragenter="handleDragEnter"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
          >
            <textarea
              ref="textareaEl"
              v-model="content"
              class="composer-textarea"
              :placeholder="placeholder"
              :disabled="loading"
              @input="adjustTextareaHeight"
            ></textarea>

            <!-- Drag Overlay -->
            <div v-if="isDragging" class="drag-overlay">
              <div class="drag-overlay-content">
                <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" class="drag-icon" />
                <p>Drop files here</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detected Items as Pills -->
      <div v-if="items.length > 0 && !hideItems" class="detected-items-section">
        <div class="section-divider"></div>

        <div class="items-container">
          <div
            v-for="(item, index) in items"
            :key="`item-${index}`"
            class="item-pill"
            :class="{ 'expanded': item.expanded }"
          >
            <!-- Collapsed State -->
            <div class="item-pill-header" @click="toggleItemExpanded(index)">
              <span class="item-type-badge">{{ item.type }}</span>
              <span class="item-value">{{ item.value }}</span>
              <span v-if="item.source === 'auto'" class="auto-badge">auto</span>
              <button
                v-if="!item.locked"
                type="button"
                class="btn-remove-pill"
                @click.stop="removeItem(index)"
                :disabled="loading"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>

            <!-- Expanded State (inline editing) -->
            <div v-if="item.expanded" class="item-pill-body">
              <div class="item-inline-fields">
                <!-- Attribution -->
                <div class="inline-field">
                  <label>Attribution</label>
                  <div class="provider-select-container">
                    <input
                      v-model="item.providerSearch"
                      type="text"
                      placeholder="Provider..."
                      class="inline-input"
                      :disabled="loading || providersLoading"
                      @input="handleItemProviderInput(item, $event)"
                      @focus="handleItemProviderFocus(item, $event)"
                      @blur="item.showProviderDropdown = false"
                      autocomplete="off"
                    />
                    <button
                      v-if="item.selectedProvider"
                      type="button"
                      class="clear-provider-btn-inline"
                      @click="clearItemProvider(item)"
                      :disabled="loading"
                    >
                      <font-awesome-icon :icon="['fas', 'xmark']" />
                    </button>

                    <div
                      v-if="item.showProviderDropdown && getFilteredProvidersForItem(item).length > 0"
                      class="provider-dropdown"
                      :style="{
                        top: item.dropdownTop ? `${item.dropdownTop}px` : 'auto',
                        left: item.dropdownLeft ? `${item.dropdownLeft}px` : 'auto',
                        width: item.dropdownWidth ? `${item.dropdownWidth}px` : 'auto'
                      }"
                    >
                      <div
                        v-for="provider in getFilteredProvidersForItem(item)"
                        :key="provider.name"
                        class="provider-option"
                        @mousedown.prevent="selectItemProvider(item, provider)"
                      >
                        <ProviderThumbnail
                          :data="{
                            name: provider.display_name || provider.name,
                            thumbnail_url: provider.thumbnail_url
                          }"
                        />
                        <span class="provider-option-name">{{ provider.display_name || provider.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Category -->
                <div class="inline-field">
                  <label>Category</label>
                  <select v-model="item.category" class="inline-select" :disabled="loading">
                    <option :value="null">None</option>
                    <option v-for="cat in availableCategories" :key="cat.id" :value="cat.id">
                      {{ cat.name }}
                    </option>
                  </select>
                </div>

                <!-- Description -->
                <div class="inline-field full-width">
                  <label>Description</label>
                  <input
                    v-model="item.description"
                    type="text"
                    placeholder="Additional context..."
                    class="inline-input"
                    maxlength="500"
                    :disabled="loading"
                  />
                  <span class="inline-char-count">{{ item.description.length }}/500</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- File Attachments Section -->
      <div v-if="uploadedFiles.length > 0" class="files-section">
        <div class="section-divider"></div>
        <h4 class="files-section-header">
          <font-awesome-icon :icon="['fas', 'paperclip']" />
          File Attachments ({{ uploadedFiles.length }})
        </h4>
        <div class="files-container">
          <FileAttachmentItem
            v-for="(file, index) in uploadedFiles"
            :key="`file-${index}-${file.name}`"
            :file="file"
            :attachment="uploadedAttachments[index]"
            :categories="availableCategories"
            v-model="fileMetadata[index]"
            :disabled="loading || uploadingFiles"
            @remove="removeFile(index)"
          />
        </div>
      </div>

      <!-- Twitter-style Toolbar -->
      <div class="composer-toolbar">
        <div class="toolbar-left" v-if="!hideItems">
          <!-- Media buttons -->
          <button
            type="button"
            class="toolbar-btn"
            @click="openFileUpload"
            :disabled="loading || uploadingFiles"
            title="Attach files"
          >
            <font-awesome-icon :icon="['fas', 'paperclip']" />
            <span v-if="uploadedFiles.length > 0" class="file-count-badge">{{ uploadedFiles.length }}</span>
          </button>

          <button
            type="button"
            class="toolbar-btn"
            @click="openBulkAdd"
            :disabled="loading"
            title="Bulk add items"
          >
            <font-awesome-icon :icon="['fas', 'list']" />
          </button>
        </div>

        <div class="toolbar-right">
          <!-- Character counter (circular) -->
          <div
            class="char-counter-circular"
            :class="{
              'warning': remainingChars < 50 && isContentValid,
              'danger': !isContentValid
            }"
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="rgba(255,255,255,0.1)"
                stroke-width="2"
                fill="none"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                :stroke="!isContentValid ? '#fc8c88' : (remainingChars < 50 ? '#fbbf24' : '#10B981')"
                stroke-width="2"
                fill="none"
                :stroke-dasharray="`${(content.length / maxChars) * 62.8} 62.8`"
                transform="rotate(-90 12 12)"
              />
            </svg>
            <span v-if="remainingChars < 50" class="char-count">{{ remainingChars }}</span>
          </div>

          <!-- Post button -->
          <button
            type="button"
            class="btn-post-twitter"
            @click="submitPost"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading">
              <font-awesome-icon :icon="['fas', 'spinner']" spin />
            </span>
            <span v-else>Post</span>
          </button>
        </div>
      </div>

      <!-- Hidden file inputs -->
      <input
        ref="fileInput"
        type="file"
        multiple
        style="display: none;"
        @change="handleFileSelect"
      />
    </div>

    <!-- Bulk Add Modal -->
    <div v-if="showBulkAddModal" class="bulk-add-modal-overlay" @click.self="showBulkAddModal = false">
      <div class="bulk-add-modal">
        <div class="bulk-add-header">
          <h3>Bulk Add Items</h3>
          <button class="btn-close-modal" @click="showBulkAddModal = false">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div class="bulk-add-body">
          <p class="bulk-add-help">
            Paste or type multiple IPs, domains, or URLs. One per line or space-separated.
          </p>

          <div class="filename-input-group">
            <label class="filename-label">Filename</label>
            <div class="filename-input-wrapper">
              <input
                v-model="bulkAddFilename"
                type="text"
                class="filename-input"
                placeholder="bulk"
                @input="sanitizeFilename"
              />
              <span class="filename-extension">.txt</span>
            </div>
          </div>

          <textarea
            v-model="bulkAddText"
            @input="processBulkText"
            class="bulk-add-textarea"
            placeholder="1.2.3.4&#10;example.com&#10;https://malicious.site"
            rows="10"
          ></textarea>
        </div>

        <div class="bulk-add-footer">
          <button class="btn-cancel" @click="showBulkAddModal = false">
            Cancel
          </button>
          <button
            class="btn-add-bulk"
            @click="confirmBulkAdd"
            :disabled="bulkAddPreview.length === 0 || !bulkAddFilename.trim()"
          >
            Create file with {{ bulkAddPreview.length }} items
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.submission-composer {
  min-width: 600px;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.composer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  max-height: calc(90vh - 100px);
  padding-right: 0.5rem;
}

/* Custom scrollbar */
.composer-content::-webkit-scrollbar {
  width: 8px;
}

.composer-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.composer-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.composer-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Twitter-style Header */
.composer-header {
  display: flex;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  min-height: 150px;
}

.composer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7357D2, #8B6FE6);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
}

.composer-main {
  flex: 1;
  min-width: 0;
}

/* Drag and Drop Zone */
.drag-drop-zone {
  position: relative;
  min-height: 60px;
}

.drag-drop-zone.dragging {
  border-radius: 8px;
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(115, 87, 210, 0.15);
  border: 2px dashed #7357D2;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.drag-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #7357D2;
}

.drag-icon {
  font-size: 2rem;
}

.drag-overlay p {
  margin: 0;
  font-weight: 600;
  font-size: 1rem;
}

.composer-textarea {
  width: 100%;
  min-height: 60px;
  max-height: 400px;
  background: transparent;
  border: none;
  color: #E5E7EB;
  font-size: 1.05rem;
  resize: none;
  padding: 0;
  line-height: 1.5;
  font-family: inherit;
  overflow-y: auto;
}

.composer-textarea:focus {
  outline: none;
}

.composer-textarea::placeholder {
  color: #6B7280;
}

/* Parent Context */
.parent-context {
  background: rgba(255, 255, 255, 0.02);
  border-left: 3px solid #7357D2;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.parent-context-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9CA3AF;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.parent-context-header svg {
  color: #7357D2;
}

.parent-context-content {
  color: #6B7280;
  font-size: 0.875rem;
  line-height: 1.5;
  font-style: italic;
}

/* Messages */
.error-message {
  padding: 0.75rem 1rem;
  background-color: rgba(252, 140, 136, 0.1);
  border: 1px solid rgba(252, 140, 136, 0.2);
  border-radius: 0.5rem;
  color: #fc8c88;
  font-size: 0.875rem;
  margin-left: 48px;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  background-color: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 0.5rem;
  color: #10B981;
  font-size: 1rem;
  font-weight: 500;
}

/* File Attachments Section */
.files-section {
  position: relative;
  margin-top: 1rem;
}

.files-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9CA3AF;
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.files-section-header svg {
  color: #7357D2;
  font-size: 0.8rem;
}

.files-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Detected Items Pills */
.detected-items-section {
  position: relative;
}

.section-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 1rem 0;
}

.items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.item-pill {
  background: rgba(115, 87, 210, 0.1);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.2s;
}

.item-pill.expanded {
  background: rgba(115, 87, 210, 0.15);
  border-radius: 12px;
  width: 100%;
  overflow: visible;
}

.item-pill-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
}

.item-pill-header:hover {
  background: rgba(115, 87, 210, 0.05);
}

.item-type-badge {
  font-size: 0.65rem;
  font-weight: 700;
  color: #7357D2;
  text-transform: uppercase;
  padding: 0.15rem 0.4rem;
  background: rgba(115, 87, 210, 0.2);
  border-radius: 10px;
}

.item-value {
  font-size: 0.85rem;
  color: #E5E7EB;
  font-family: 'Monaco', 'Courier New', monospace;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auto-badge {
  font-size: 0.65rem;
  color: #10B981;
  font-weight: 600;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.15rem 0.4rem;
  border-radius: 10px;
}

.btn-remove-pill {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  padding: 0;
  font-size: 0.8rem;
}

.btn-remove-pill:hover:not(:disabled) {
  background: rgba(252, 140, 136, 0.2);
  color: #fc8c88;
}

.btn-remove-pill:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.item-pill-body {
  padding: 0.75rem;
  border-top: 1px solid rgba(115, 87, 210, 0.2);
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.item-inline-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.inline-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
}

.inline-field.full-width {
  grid-column: 1 / -1;
}

.inline-field label {
  font-size: 0.7rem;
  color: #9CA3AF;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.inline-input,
.inline-select {
  padding: 0.5rem;
  background: #171C24;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #E5E7EB;
  font-size: 0.85rem;
  transition: border-color 0.2s;
}

.inline-input:focus,
.inline-select:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.5);
}

.inline-input::placeholder {
  color: #6B7280;
}

.inline-char-count {
  font-size: 0.7rem;
  color: #6B7280;
  text-align: right;
  margin-top: 0.25rem;
}

.clear-provider-btn-inline {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  font-size: 0.7rem;
}

.clear-provider-btn-inline:hover:not(:disabled) {
  color: #E5E7EB;
  background: rgba(255, 255, 255, 0.05);
}

/* Provider Selection (shared) */
.provider-select-container {
  position: relative;
  z-index: 100;
}

.clear-provider-btn {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 1rem;
}

.clear-provider-btn:hover:not(:disabled) {
  color: #E5E7EB;
}

.clear-provider-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.provider-dropdown {
  position: fixed;
  background-color: #1B2029;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  max-height: 250px;
  overflow-y: auto;
  z-index: 10000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.provider-dropdown::-webkit-scrollbar {
  width: 8px;
}

.provider-dropdown::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.provider-dropdown::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.provider-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.provider-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.provider-option:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.provider-option-name {
  color: #E5E7EB;
  font-size: 0.875rem;
}

/* Channel Selector Section */
.channel-selector-section {
  display: flex;
  align-items: center;
}

.channel-label {
  font-size: 0.85rem;
  color: #9CA3AF;
  font-weight: 500;
}

.channel-select {
  background: transparent;
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 16px;
  color: #7357D2;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.channel-select:hover {
  background: rgba(115, 87, 210, 0.05);
}

.channel-select:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.5);
}

/* Twitter-style Toolbar */
.composer-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.toolbar-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #7357D2;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1.1rem;
  position: relative;
}

.toolbar-btn:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.1);
  color: #8B6FE6;
}

.toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.file-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #7357D2;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.35rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  line-height: 1;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.char-counter-circular {
  position: relative;
  width: 24px;
  height: 24px;
}

.char-count {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 600;
  color: #9CA3AF;
}

.char-counter-circular.warning .char-count {
  color: #fbbf24;
}

.char-counter-circular.danger .char-count {
  color: #fc8c88;
}

.btn-post-twitter {
  background: #7357D2;
  color: white;
  border: none;
  border-radius: 18px;
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-post-twitter:hover:not(:disabled) {
  background: #8B6FE6;
}

.btn-post-twitter:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bulk Add Modal */
.bulk-add-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.bulk-add-modal {
  background: #1E252F;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.bulk-add-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.bulk-add-header h3 {
  margin: 0;
  color: #FFFFFF;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close-modal {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  font-size: 1.2rem;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.bulk-add-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.bulk-add-help {
  margin: 0 0 1rem 0;
  color: #9CA3AF;
  font-size: 0.9rem;
}

.filename-input-group {
  margin-bottom: 1rem;
}

.filename-label {
  display: block;
  font-size: 0.85rem;
  color: #9CA3AF;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.filename-input-wrapper {
  display: flex;
  align-items: center;
  background: #171C24;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  transition: border-color 0.2s;
}

.filename-input-wrapper:focus-within {
  border-color: rgba(115, 87, 210, 0.5);
}

.filename-input {
  flex: 1;
  background: transparent;
  border: none;
  color: #E5E7EB;
  font-size: 0.9rem;
  font-family: 'Monaco', 'Courier New', monospace;
  outline: none;
  min-width: 0;
}

.filename-input::placeholder {
  color: #6B7280;
}

.filename-extension {
  color: #6B7280;
  font-size: 0.9rem;
  font-family: 'Monaco', 'Courier New', monospace;
  margin-left: 0.25rem;
  user-select: none;
}

.bulk-add-textarea {
  width: 100%;
  min-height: 200px;
  padding: 0.75rem;
  background: #171C24;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #E5E7EB;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  resize: vertical;
}

.bulk-add-textarea:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.5);
}

.bulk-add-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-cancel {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #9CA3AF;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.btn-add-bulk {
  background: #7357D2;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-bulk:hover:not(:disabled) {
  background: #8B6FE6;
}

.btn-add-bulk:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .submission-composer {
    min-width: auto;
    max-width: 100%;
  }

  .composer-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .composer-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }

  .detected-items-section {
    margin-left: 0;
  }

  .error-message {
    margin-left: 0;
  }

  .channel-selector-section {
    flex-wrap: wrap;
  }

  .composer-toolbar {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .toolbar-left {
    flex: 1 1 100%;
    justify-content: space-between;
  }

  .toolbar-right {
    flex: 1 1 100%;
    justify-content: space-between;
  }

  .item-inline-fields {
    grid-template-columns: 1fr;
  }
}
</style>
